import { Injectable } from '@angular/core';
import { combineLatest, forkJoin, Observable, of, Subject } from 'rxjs';
import { map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { User } from '../../models/identity/user.model';
import { AccountsService } from '../../services/identity/accounts.service';
import { UserService } from '../../services/identity/user.service';
import { ConversationView } from '../type/conversation-view.type';
import { Conversation } from '../type/conversation.type';
import { Message } from '../type/message.type';
import { ChatNotificationService } from './chat-notification.service';
import { ChatSignalrService } from './chat-signalr.service';
import { ChatStateService } from './chat-state.service';
import { ChatService } from './chat.service';

@Injectable({
    providedIn: 'root',
})
export class ChatFacadeService {
    readonly CONVERSATION_NEW_PLACEHOLDER = 'new';
    private readonly destroy$ = new Subject<void>();

    // Expose state selectors
    readonly conversations$ = this.chatState.conversations$;
    readonly activeConversationId$ = this.chatState.activeConversationId$;
    readonly activeConversation$ = this.chatState.activeConversation$;
    readonly activeConversationView$ = this.chatState.activeConversationView$;
    readonly activeMessages$ = this.chatState.activeMessages$;
    readonly isLoading$ = this.chatState.isLoading$;
    readonly targetUser$ = this.chatState.targetUser$;

    readonly hasUnreadMessages$ = this.chatState.hasUnreadMessages$;
    private isInitialized = false;
    constructor(
        private readonly chatState: ChatStateService,
        private readonly chatService: ChatService,
        private readonly chatSignalR: ChatSignalrService,
        private readonly accountsService: AccountsService,
        private readonly userService: UserService,
        private readonly chatNotificationService: ChatNotificationService,
    ) {}

    /**
     * Initialize chat: start SignalR connection and subscribe to events
     */
    async initializeChat(): Promise<void> {
        if (this.isInitialized) {
            return;
        }
        await this.chatSignalR.startConnection();
        this.isInitialized = true;

        // Request notification permission from user
        try {
            await this.chatNotificationService.requestPermission();
        } catch (error) {
            console.warn('Failed to request notification permission:', error);
        }

        // Subscribe to SignalR events with takeUntil to prevent memory leaks
        this.chatSignalR.messageReceived$.pipe(takeUntil(this.destroy$)).subscribe((message: Message) => {
            this.handleMessageReceived(message);
        });

        this.chatSignalR.conversationStarted$.pipe(takeUntil(this.destroy$)).subscribe((event) => {
            this.handleConversationStarted(event.groupId, event.initiatorId, event.message);
        });
    }

    /**
     * Load user's conversations and enrich with target user info
     */
    loadMyConversations(): Observable<ConversationView[]> {
        this.chatState.setLoading(true);

        return this.chatService.getMyConversations().pipe(
            switchMap((conversations) => {
                if (!conversations || conversations.length === 0) {
                    return of([]);
                }

                // Enrich conversations with user info
                const conversationViews$ = conversations.map((conversation) => this.enrichConversationWithUser(conversation));

                return forkJoin(conversationViews$);
            }),
            tap((conversationViews) => {
                this.chatState.setConversations(conversationViews);
                this.chatState.setLoading(false);
                conversationViews.forEach((view) => {
                    this.chatSignalR
                        .joinChatGroup(view.conversation.id)
                        .catch((err) => console.error(`Failed to join group ${view.conversation.id}`, err));
                });
            }),
        );
    }

    /**
     * Select a conversation and load its messages
     */
    selectConversation(conversationId: string): Observable<Message[]> {
        this.chatState.setActiveConversation(conversationId);
        this.chatService.markConversationAsRead(conversationId).subscribe();
        this.chatState.resetUnreadCount(conversationId);
        return this.chatService.getConversationMessages(conversationId).pipe(
            tap((messages) => {
                this.chatState.setMessages(conversationId, messages);
            }),
            switchMap(() => this.chatSignalR.joinChatGroup(conversationId)),
            switchMap(() => this.activeMessages$.pipe(take(1))),
        );
    }

    /**
     * Select conversation by ConversationView
     */
    selectConversationView(conversationView: ConversationView): Observable<Message[]> {
        this.chatState.setTargetUser(conversationView.targetUser);
        return this.selectConversation(conversationView.conversation.id);
    }

    /**
     * Go back to conversation list
     */
    goBackToHistory(): void {
        this.chatState.setActiveConversation(null);
        this.chatState.setTargetUser(null);
    }

    /**
     * Send message to active conversation
     */
    sendMessage(conversationId: string, content: string): void {
        if (!content.trim()) return;

        // Verify that the conversation exists in local state before sending
        // This prevents messages to non-existent or unconfirmed conversations
        this.conversations$.pipe(take(1)).subscribe((conversations) => {
            const exists = conversations.some((cv) => cv.conversation.id === conversationId);
            if (!exists) {
                console.error(`Conversation ${conversationId} not found in local state`);
                return;
            }
            this.chatSignalR.sendMessage(conversationId, content);
        });
    }

    /**
     * Start new conversation with a user
     */
    startConversation(targetUserId: string, content: string): void {
        if (!content.trim()) return;
        this.chatSignalR.startConversation(targetUserId, content);
    }

    /**
     * Open conversation with a specific user (from external trigger)
     */
    openConversationWithUser(user: User): Observable<ConversationView | null> {
        return this.conversations$.pipe(
            take(1),
            map((conversations) => {
                // Check if conversation already exists
                const existingConversation = conversations.find((cv) => cv.conversation.participants.some((pid) => pid === user.userId));

                if (existingConversation) {
                    // Select existing conversation
                    this.selectConversationView(existingConversation).subscribe();
                    return existingConversation;
                } else {
                    // Prepare new conversation - set user as target
                    // This will trigger the UI to show the conversation view with empty messages
                    this.chatState.setTargetUser(user);
                    // Set a temporary "new" conversation state to trigger conversation view
                    this.chatState.setActiveConversation(this.CONVERSATION_NEW_PLACEHOLDER);

                    return null;
                }
            }),
        );
    }

    /**
     * Cleanup when destroying chat
     */
    async cleanup(): Promise<void> {
        this.destroy$.next();
        this.destroy$.complete();
        try {
            await this.chatSignalR.stopConnection();
        } finally {
            this.chatState.reset();
        }
    }

    private enrichConversationWithUser(conversation: Conversation): Observable<ConversationView> {
        const otherUserId = conversation.participants.find((id) => id !== this.accountsService.currentUser.userId);

        if (!otherUserId) {
            return of({ conversation, targetUser: null });
        }

        return this.userService.getUserById(otherUserId).pipe(map((user) => ({ conversation, targetUser: user })));
    }

    private updateConversationState(message: Message): void {
        this.chatState.addMessage(message.conversationId, message);
        this.chatState.updateLastMessage(message.conversationId, message);
    }

    private notifyInactiveConversation(message: Message, conversations: ConversationView[]): void {
        this.chatState.incrementUnreadCount(message.conversationId);
        this.chatNotificationService.playNotificationSound();

        const conversationView = conversations.find((cv) => cv.conversation.id === message.conversationId);
        if (conversationView?.targetUser) {
            const onClick = (): void => {
                this.selectConversation(message.conversationId).subscribe();
            };
            this.chatNotificationService.showBrowserNotification(conversationView.targetUser, message, onClick);
        }
    }

    private handleMessageReceived(message: Message): void {
        const currentUserId = this.accountsService.currentUser.userId;
        const isOwnMessage = message.senderId === currentUserId;

        // Always update state first
        this.updateConversationState(message);

        if (isOwnMessage) {
            // Own message: mark as read, no notification
            this.chatService.markConversationAsRead(message.conversationId).subscribe();
            return;
        }

        // Get active conversation and conversations list in a single subscription
        combineLatest([this.chatState.activeConversationId$, this.chatState.conversations$])
            .pipe(take(1))
            .subscribe(([activeConversationId, conversations]) => {
                const isActiveConversation = message.conversationId === activeConversationId;

                if (isActiveConversation) {
                    // Active conversation: mark as read, no notification
                    this.chatService.markConversationAsRead(message.conversationId).subscribe();
                } else {
                    // Inactive conversation: notify user
                    this.notifyInactiveConversation(message, conversations);
                }
            });
    }

    private handleConversationStarted(groupId: string, initiatorId: string, message: string): void {
        const currentUserId = this.accountsService.currentUser.userId;
        const isInitiator = currentUserId === initiatorId;

        // Reload conversations to get the new one
        this.loadMyConversations()
            .pipe(
                switchMap((conversationViews) => {
                    const newConvView = conversationViews.find((cv) => cv.conversation.id === groupId);
                    if (!newConvView) {
                        return of(null);
                    }

                    if (!isInitiator) {
                        // Non-initiator: just join the group
                        this.chatSignalR
                            .joinChatGroup(groupId)
                            .catch((err) => console.error(`Failed to join group ${groupId}`, err));
                        return of(null);
                    }

                    // Initiator: check active conversation state
                    return this.chatState.activeConversationId$.pipe(
                        take(1),
                        tap((activeId) => {
                            if (activeId === this.CONVERSATION_NEW_PLACEHOLDER) {
                                // New conversation flow: activate and send first message
                                this.chatState.setMessages(groupId, []);
                                this.chatState.setActiveConversation(groupId);
                                this.chatState.setTargetUser(newConvView.targetUser);
                                this.chatSignalR.joinGroupReady(groupId, message, initiatorId);
                            } else {
                                // Another tab/session: just join the group
                                this.chatSignalR
                                    .joinChatGroup(groupId)
                                    .catch((err) => console.error(`Failed to join group ${groupId}`, err));
                            }
                        }),
                    );
                }),
            )
            .subscribe();
    }
}
